import * as React from 'react'

import { EnvironmentContext } from 'config/environment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'

import './index.styles.css'
import { overrideHapiElementsStyles } from './overrideHapiElementsStyles'
import { useGenerateVonqClientToken } from './useGenerateVonqClientToken'

interface VonqContextState {
  elementsLoaded: boolean
  clientId: string
}

const VonqContext = React.createContext<VonqContextState>({ elementsLoaded: false, clientId: '' })

export const useHapiElementsContext = () => {
  const context = React.useContext(VonqContext)

  if (context === undefined) {
    throw new Error('useHapiElementsContext must be used within a VonqContext')
  }
  return context
}

export const HapiElementsLoader = ({ children }: React.PropsWithChildren) => {
  const { hapiElementsUrl } = React.useContext(EnvironmentContext)
  const { company } = useCurrentSession()
  const [elementsLoaded, setElementsLoaded] = React.useState(
    !!document.getElementById('hapi-loader')
  )
  const { generateToken, clientToken } = useGenerateVonqClientToken()
  const token = clientToken?.token
  const clientId = clientToken?.clientId || ''

  const onLoadHapiElementsInjector = async (clientToken: string) => {
    try {
      window.hapiInjector.setConfig('clientToken', clientToken)
      // Inject is a Promise that resolves when HAPI Elements script has successfully loaded
      await window.hapiInjector.inject()

      overrideHapiElementsStyles()

      setElementsLoaded(true)
    } catch (error) {
      // Swallow error
    }
  }

  useEffect(() => {
    if (!elementsLoaded) {
      generateToken(company.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id])

  useEffect(() => {
    if (!token) {
      return
    }

    window.addEventListener('hapi:load:injector', () => onLoadHapiElementsInjector(token))

    return () => {
      window.removeEventListener('hapi:load:injector', () => onLoadHapiElementsInjector(token))
    }
  }, [token])

  const scriptSrc = `${hapiElementsUrl}/api/injector.js`

  useEffect(() => {
    if (token && !document.getElementById('hapi-loader')) {
      const scriptEl = document.createElement('script')
      scriptEl.src = scriptSrc
      scriptEl.id = 'hapi-loader'
      scriptEl.type = 'module'
      scriptEl.defer = true

      document.body.appendChild(scriptEl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => {
    if (!elementsLoaded) {
      return
    }

    const stepKeys = window.hapi?.orderJourney?.utils?.stepKeys

    if (!stepKeys) return

    const STEPS_TO_DISABLE = [stepKeys.recruiterInfo, stepKeys.postingContactInfo]

    window.hapi.ui.service.onIframeLoaded((iframeName: string) => {
      if (iframeName.includes('he-orderjourney-checkout-shorter')) {
        window.hapi.orderJourney.state.stepsEnabled =
          window.hapi.orderJourney.state.stepsEnabled.value.filter((stepsArr: string[]) =>
            stepsArr.some((key) => !STEPS_TO_DISABLE.includes(key))
          )
      }
    })
  }, [elementsLoaded])

  return (
    <VonqContext.Provider value={{ elementsLoaded: elementsLoaded, clientId }}>
      {children}
    </VonqContext.Provider>
  )
}
